/**
 * Tipos para DERAG3R2Service — Infraestructura hidráulica
 *
 * Datos: DERA G3 (4 capas puntuales) — IECA Junta de Andalucía
 * Incluye: Manantial, Captación, Depósito, Estación de Aforo
 * CRS: EPSG:25830 (UTM30 ETRS89)
 * Ruta R2: /dera-g3/{ine5}.json.gz
 *
 * @module types/deraG3
 * @version 1.0.0
 */

// =============================================================================
// FEATURE — Esquema DERAFeaturePTEL (generado por dera-g3-download.ts)
// =============================================================================

/** Feature G3 almacenada en R2 */
export interface DERAG3Feature {
  /** ID DERA original */
  id: string;
  /** Nombre de la infraestructura */
  nombre: string;
  /** Coordenada X UTM30 EPSG:25830 */
  x: number;
  /** Coordenada Y UTM30 EPSG:25830 */
  y: number;
  /** Código INE municipio (5 dígitos) */
  cod_ine: string;
  /** Nombre municipio */
  municipio: string;
  /** Provincia */
  provincia: string;
  /** Capa DERA de origen (ej: "g03_09_Deposito") */
  capa: string;
  /** Subtipo PTEL (ej: "DEPOSITO", "MANANTIAL") */
  subtipo: string;
  /** Metadatos extra (red, cota, capacidad, cuenca…) */
  meta?: Record<string, string | number>;
}

// =============================================================================
// SUBTIPOS G3
// =============================================================================

/** Subtipos de infraestructura G3 */
export type DERAG3Subtipo =
  | 'MANANTIAL'
  | 'CAPTACION'
  | 'DEPOSITO'
  | 'ESTACION_AFORO';

/** Mapeo subtipo → tipología PTEL */
export const G3_SUBTIPO_TO_TIPOLOGIA: Record<DERAG3Subtipo, string> = {
  MANANTIAL: 'ABASTECIMIENTO',
  CAPTACION: 'ABASTECIMIENTO',
  DEPOSITO: 'ABASTECIMIENTO',
  ESTACION_AFORO: 'HIDROLOGIA',
};

// =============================================================================
// OPCIONES Y RESULTADOS
// =============================================================================

/** Opciones para búsqueda en G3 R2 */
export interface DERAG3SearchOptions {
  /** Código INE municipio (5 dígitos) — REQUERIDO */
  codINE: string;
  /** Nombre infraestructura para fuzzy match */
  name?: string;
  /** Filtrar por subtipo G3 */
  subtipo?: string;
  /** Umbral mínimo confianza (0-100, default: 60) */
  minConfidence?: number;
  /** Máximo resultados (default: 5) */
  maxResults?: number;
}

/** Resultado de búsqueda G3 R2 */
export interface DERAG3Result {
  /** Coordenada X UTM30 */
  x: number;
  /** Coordenada Y UTM30 */
  y: number;
  /** Confianza (0-100) */
  confidence: number;
  /** Precisión estimada metros */
  precision: number;
  /** Identificador de fuente */
  source: 'dera_g3_r2';
  /** Feature encontrada */
  feature: DERAG3Feature;
  /** Score fuzzy (0-1) si aplica */
  fuzzyScore?: number;
}

// =============================================================================
// CONSTANTES VALIDACIÓN
// =============================================================================

/** Rango válido X UTM30 Andalucía */
export const G3_X_RANGE = { min: 100_000, max: 700_000 };
/** Rango válido Y UTM30 Andalucía */
export const G3_Y_RANGE = { min: 3_950_000, max: 4_300_000 };
/** Precisión estimada DERA G3 (metros) — coordenadas oficiales */
export const G3_PRECISION_M = 10;
